import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2, Info, FileText, ChevronLeft, Package, Store, DollarSign, Receipt, CheckCircle, XCircle } from 'lucide-react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import TextArea from '../components/ui/TextArea';
import Checkbox from '../components/ui/Checkbox';
import FastAPIClient from '../services/apiClient';
import { useToast } from '../hooks/useToast';
import { fetchPurchaseOrdersForVendor, fetchCurrencies, fetchAllBankAccounts } from '../apiHelper';

const client = new FastAPIClient();

/**
 * VendorGoodsReceipt Component.
 * Facilitates the recording of received goods against existing Purchase Orders.
 * Now includes grand total and paid amount (partial payment allowed).
 */
const VendorGoodsReceipt = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const location = useLocation();
    const vendorId = location.state?.vendor_id;
    const vendorName = location.state?.vendor_name || 'Selected Vendor';
    const ponumber = location.state?.ponumber;

    const [user, setUser] = useState(null);
    const [purchaseOrders, setPurchaseOrders] = useState([]);
    const [currencies, setCurrencies] = useState([]);
    const [bankAccountOptions, setBankAccountOptions] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [dataErrors, setDataErrors] = useState(null);
    const [loadingBankAccounts, setLoadingBankAccounts] = useState(false);
    const [errorBankAccounts, setErrorBankAccounts] = useState(null);

    const [selectedPO, setSelectedPO] = useState(null);
    const [receivedItems, setReceivedItems] = useState([]);
    const [selectedCurrency, setSelectedCurrency] = useState('');
    const [selectedBankAccount, setSelectedBankAccount] = useState('');
    const [isCreditReceipt, setIsCreditReceipt] = useState(false);
    const [paidAmount, setPaidAmount] = useState(''); // NEW: amount paid to vendor now
    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // NEW: Calculate grand total of items being received (qty × cost)
    const grandTotal = useMemo(() => {
        return receivedItems.reduce((sum, item) => {
            const qty = parseFloat(item.qtyreceived_input) || 0;
            const cost = parseFloat(item.cost) || 0;
            return sum + (qty * cost);
        }, 0);
    }, [receivedItems]);

    // NEW: Whenever grandTotal changes, update paidAmount to match (unless user has edited it)
    useEffect(() => {
        if (!paidAmount || parseFloat(paidAmount) === grandTotal || grandTotal === 0) {
            setPaidAmount(grandTotal.toFixed(2));
        }
    }, [grandTotal]);

    // Effect to load user from local storage
    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            showToast("Please log in to record goods receipts.", "info");
            navigate('/login');
        } else {
            setUser(JSON.parse(lcUser));
        }
    }, [showToast, navigate]);

    // Fetch initial data...
    useEffect(() => {
        const loadInitialData = async () => {
            if (!user) return;
            if (!vendorId) {
                setDataErrors("No vendor selected. Please go back and select a vendor.");
                setLoadingData(false);
                return;
            }

            setLoadingData(true);
            setDataErrors(null);
            try {
                const [fetchedPOs, fetchedCurrencies, fetchedAccounts] = await Promise.all([
                    fetchPurchaseOrdersForVendor(vendorId),
                    fetchCurrencies(user),
                    fetchAllBankAccounts()
                ]);

                setPurchaseOrders(fetchedPOs);
                setCurrencies(fetchedCurrencies.map(curr => ({
                    label: `${curr.setccurr} (${curr.setcnote})`,
                    value: curr.setccurr,
                    data: curr
                })));
                setBankAccountOptions(fetchedAccounts.map(account => ({
                    label: `${account.name}`,
                    value: account.account_id
                })));

                if (fetchedCurrencies.length > 0) {
                    setSelectedCurrency(fetchedCurrencies[0].setccurr);
                }
                if (fetchedAccounts.length > 0) {
                    setSelectedBankAccount(fetchedAccounts[0].account_id);
                }

                if (ponumber) {
                    const preselectedPO = fetchedPOs.find(po => po.ponumber === ponumber);
                    if (preselectedPO) {
                        setSelectedPO(preselectedPO);
                    } else {
                        showToast(`Purchase Order ${ponumber} not found.`, "error");
                    }
                }

            } catch (err) {
                console.error("Error loading initial data for goods receipt:", err);
                const errorMessage = err.response?.data?.detail || `Failed to load data: ${err.message || err}`;
                setDataErrors(errorMessage);
                showToast(errorMessage, "error");
            } finally {
                setLoadingData(false);
            }
        };

        loadInitialData();
    }, [user, vendorId, ponumber, showToast]);

    // When PO selected → initialize receivedItems
    useEffect(() => {
        if (selectedPO && selectedPO.poitems) {
            setReceivedItems(selectedPO.poitems.map(item => ({
                podetail_id: item.podetail_id,
                catentry_id: item.catentry_id,
                qtyordered: item.qtyordered,
                qtyreceived_current: item.qtyreceived,
                qtyremaining: item.qtyremaining,
                qtyreceived_input: '',
                listprice: item.catentry.listprice,
                cost: '',
                selling_price: '',
                comment: '',
                productname: item.catentry?.productname || 'N/A',
                partnumber: item.catentry?.partnumber || 'N/A',
                store_id: selectedPO.store_id,
                vendor_id: selectedPO.vendor_id,
            })));
            setPaidAmount('0.00'); // reset when new PO selected
        } else {
            setReceivedItems([]);
        }
        setFormErrors({});
    }, [selectedPO]);

    const handlePOSelect = useCallback((e) => {
        const selectedOption = purchaseOrders.find(po => po.purchaseorder_id === parseInt(e.target.value));
        setSelectedPO(selectedOption);
    }, [purchaseOrders]);

    const handleReceivedItemChange = useCallback((podetailId, field, value) => {
        setReceivedItems(prevItems => prevItems.map(item => {
            if (item.podetail_id === podetailId) {
                return { ...item, [field]: value };
            }
            return item;
        }));
    }, []);

    // Validation (updated to include paid amount check)
    const validateForm = useCallback(() => {
        let newErrors = {};
        let isValid = true;

        if (!selectedPO) {
            newErrors.poSelection = "Please select a Purchase Order.";
            isValid = false;
        }
        if (!selectedCurrency) {
            newErrors.currency = "Currency is required.";
            isValid = false;
        }
        if (!isCreditReceipt && !selectedBankAccount) {
            newErrors.bankAccount = "Bank account is required for cash payments.";
            isValid = false;
        }

        const paid = parseFloat(paidAmount);
        if (isNaN(paid) || paid < 0) {
            newErrors.paidAmount = "Paid amount must be a valid number ≥ 0.";
            isValid = false;
        } else if (!isCreditReceipt && paid > grandTotal) {
            newErrors.paidAmount = "Paid amount cannot exceed grand total for cash receipt.";
            isValid = false;
        }

        const itemsToReceive = receivedItems.filter(item => parseFloat(item.qtyreceived_input) > 0);

        if (itemsToReceive.length === 0) {
            newErrors.general = "Please enter quantity received for at least one item.";
            isValid = false;
        }

        itemsToReceive.forEach(item => {
            let itemErrors = {};
            const qtyReceived = parseFloat(item.qtyreceived_input);
            const cost = parseFloat(item.cost);
            const sellingPrice = parseFloat(item.selling_price);

            if (isNaN(qtyReceived) || qtyReceived <= 0) {
                itemErrors.qtyreceived_input = "Quantity received must be a positive number.";
                isValid = false;
            } else if (qtyReceived > item.qtyremaining) {
                itemErrors.qtyreceived_input = `Cannot receive more than remaining (${item.qtyremaining}).`;
                isValid = false;
            }

            if (isNaN(cost) || cost < 0) {
                itemErrors.cost = "Cost must be a non-negative number.";
                isValid = false;
            }

            if (isNaN(sellingPrice) || sellingPrice < 0) {
                itemErrors.selling_price = "Selling Price must be a non-negative number.";
                isValid = false;
            }

            if (Object.keys(itemErrors).length > 0) {
                newErrors[item.podetail_id] = itemErrors;
                isValid = false;
            }
        });

        setFormErrors(newErrors);
        return isValid;
    }, [selectedPO, receivedItems, selectedCurrency, isCreditReceipt, selectedBankAccount, grandTotal, paidAmount]);

    // Submit handler – now includes grand_total and paid_amount
    const handleSubmitReceipt = useCallback(async (e) => {
        e.preventDefault();
        setFormErrors({});

        if (!validateForm()) {
            showToast("Please correct the errors in the form.", "error");
            return;
        }

        setIsSubmitting(true);
        try {
            const itemsToSubmit = receivedItems
                .filter(item => parseFloat(item.qtyreceived_input) > 0)
                .map(item => {
                    const qtyReceivedInput = parseFloat(item.qtyreceived_input);
                    const qtyRemainingAfterReceipt = item.qtyremaining - qtyReceivedInput;

                    return {
                        catentry_id: item.catentry_id,
                        podetail_id: item.podetail_id,
                        store_id: item.store_id,
                        setccurr: selectedCurrency,
                        vendor_id: item.vendor_id,
                        qtyreceived: qtyReceivedInput,
                        qtyinprocess: Math.max(0, qtyRemainingAfterReceipt),
                        cost: parseFloat(item.cost),
                        selling_price: parseFloat(item.selling_price),
                        comment: item.comment.trim(),
                        receipttype: 'POGR',
                        payment_method: isCreditReceipt ? 'credit' : 'cash',
                        bankaccount_id: isCreditReceipt ? null : selectedBankAccount,
                        recorder_id: user.users_id,
                    };
                });

            // Enhanced payload with totals
            const payload = {
                items: itemsToSubmit,
                grand_total: parseFloat(grandTotal.toFixed(2)),
                paid_amount: parseFloat(paidAmount),
                payment_method: isCreditReceipt ? 'credit' : 'cash',
                bankaccount_id: isCreditReceipt ? null : selectedBankAccount,
                setccurr: selectedCurrency,
                recorder_id: user.users_id,
            };

            console.log("Goods Receipt Payload:", payload);
            const response = await client.postData('/vendor/receive-goods', payload);

            showToast(response.msg || "Goods receipt recorded successfully!", "success");
            setSelectedPO(null);
            setReceivedItems([]);
            setPaidAmount('0.00');
            setSelectedCurrency(currencies.length > 0 ? currencies[0].value : '');
            setSelectedBankAccount(bankAccountOptions.length > 0 ? bankAccountOptions[0].value : '');
            setIsCreditReceipt(false);
            setFormErrors({});

        } catch (err) {
            console.error("Error recording goods receipt:", err);
            const errorMessage = err.response?.data?.detail || `Failed to record goods receipt: ${err.message || err}`;
            showToast(errorMessage, "error");
            setFormErrors({ api: errorMessage });
        } finally {
            setIsSubmitting(false);
        }
    }, [selectedPO, receivedItems, selectedCurrency, isCreditReceipt, selectedBankAccount, user, validateForm, grandTotal, paidAmount, showToast, currencies, bankAccountOptions]);

    // ... (loading/no user/no vendor UI remains unchanged)

    if (!user) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-orange-600'>
                <Info size={32} className="mb-2" />
                <p className="text-lg mb-4">Please log in to record goods receipts.</p>
            </div>
        );
    }

    if (!vendorId) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-red-600'>
                <XCircle size={32} className="mb-2" />
                <p className="text-lg mb-4">No vendor selected. Please go back to Vendor Management to select a vendor for goods receipt.</p>
                <Button onClick={() => navigate('/vendor')} variant="primary" className="mt-4">
                    <ChevronLeft size={20} className="mr-2" /> Go to Vendor Management
                </Button>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
            <Card className="p-4 sm:p-6 shadow-lg rounded-lg">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-bold text-text-primary flex items-center gap-2">
                        <Receipt size={24} className="text-green-600" /> Goods Receipt for {vendorName}
                    </h2>
                    <Button className="flex items-center justify-center px-4 py-3" onClick={() => navigate('/vendor/view-purchase-orders', { state: { vendor_id: vendorId, vendor_name: vendorName } })} variant="secondary" size="sm">
                        <ChevronLeft size={20} className="mr-2" /> Back to POs
                    </Button>
                </div>
                <p className="text-text-secondary mb-6">
                    Record received items from {vendorName} against existing Purchase Orders.
                </p>

                {loadingData ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="animate-spin h-6 w-6 mr-2 text-blue-500" />
                        <p className="text-gray-600">Loading data...</p>
                    </div>
                ) : dataErrors ? (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-4" role="alert">
                        <p>{dataErrors}</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmitReceipt} noValidate>
                        {formErrors.api && (
                            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-4" role="alert">
                                <p>{formErrors.api}</p>
                            </div>
                        )}

                        {/* PO Selection and Currency */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            <SearchableSelectBox
                                label="Select Purchase Order"
                                id="po-select"
                                name="po_id"
                                value={selectedPO?.purchaseorder_id || ''}
                                onChange={handlePOSelect}
                                options={purchaseOrders.map(po => ({
                                    label: `${po.ponumber} (Store: ${po.store?.identifier || 'N/A'}, Ordered: ${new Date(po.orderdate).toLocaleDateString()})`,
                                    value: po.purchaseorder_id,
                                    data: po
                                }))}
                                placeholder="Choose a Purchase Order"
                                required
                                error={formErrors.poSelection}
                                icon={<FileText size={18} />}
                            />
                            <SearchableSelectBox
                                label="Currency"
                                id="currency-select"
                                name="currency"
                                value={selectedCurrency}
                                onChange={(e) => setSelectedCurrency(e.target.value)}
                                options={currencies}
                                placeholder="Select currency"
                                required
                                error={formErrors.currency}
                                icon={<DollarSign size={18} />}
                            />
                        </div>

                        {/* Credit Receipt Checkbox and Bank Account */}
                        <div className="mb-6">
                            <Checkbox
                                label={isCreditReceipt ? "This is a Credit Receipt (Vendor will be paid later)" : "This is a Cash Receipt (Vendor paid now)"}
                                id="isCreditReceipt"
                                checked={isCreditReceipt}
                                onChange={(e) => setIsCreditReceipt(e.target.checked)}
                            />
                            {!isCreditReceipt && (
                                <div className="mt-4">
                                    <SearchableSelectBox
                                        label="Bank Account for Payment"
                                        value={selectedBankAccount}
                                        onChange={(e) => setSelectedBankAccount(e.target.value)}
                                        options={bankAccountOptions}
                                        placeholder="Select bank account"
                                        required
                                        error={formErrors.bankAccount}
                                        icon={<DollarSign size={18} />}
                                    />
                                </div>
                            )}
                        </div>

                        {selectedPO && (
                            <>
                                <h3 className="text-xl font-bold text-text-primary mb-4 flex items-center gap-2">
                                    <Package size={20} className="text-blue-600" /> Items to Receive for PO: {selectedPO.ponumber}
                                </h3>
                                {formErrors.general && <p className="text-gmail-red text-sm mb-4">{formErrors.general}</p>}

                                <div className="space-y-6">
                                    {receivedItems.map((item, index) => (
                                        <div key={item.podetail_id} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                                            <h4 className="font-semibold text-lg mb-3 text-text-primary">
                                                {item.productname} ({item.partnumber})
                                            </h4>
                                            <p className="text-sm text-gray-600 mb-2">
                                                Ordered: {item.qtyordered} | Already Received: {item.qtyreceived_current} | Remaining: {item.qtyremaining} | Current Selling Price: {item.listprice.currency_rel.setcnote}{item.listprice.listprice}
                                            </p>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                <Input
                                                    label="Quantity Received (this shipment)"
                                                    type="number"
                                                    value={item.qtyreceived_input}
                                                    onChange={(e) => handleReceivedItemChange(item.podetail_id, 'qtyreceived_input', e.target.value)}
                                                    error={formErrors[item.podetail_id]?.qtyreceived_input}
                                                />
                                                <Input
                                                    label="Cost (per unit)"
                                                    type="number"
                                                    step="0.01"
                                                    value={item.cost}
                                                    onChange={(e) => handleReceivedItemChange(item.podetail_id, 'cost', e.target.value)}
                                                    error={formErrors[item.podetail_id]?.cost}
                                                />
                                                <Input
                                                    label="New Selling Price (per unit)"
                                                    type="number"
                                                    step="0.01"
                                                    value={item.selling_price}
                                                    onChange={(e) => handleReceivedItemChange(item.podetail_id, 'selling_price', e.target.value)}
                                                    error={formErrors[item.podetail_id]?.selling_price}
                                                />
                                                <div className="md:col-span-3">
                                                    <TextArea
                                                        label="Comment"
                                                        placeholder="Notes for this item receipt"
                                                        value={item.comment}
                                                        onChange={(e) => handleReceivedItemChange(item.podetail_id, 'comment', e.target.value)}
                                                        rows={2}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* NEW: Grand Total and Paid Amount */}
                                <div className="mt-8 p-6 bg-blue-50 rounded-lg border border-blue-200">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <p className="text-lg font-semibold text-text-primary">
                                                Grand Total ({selectedCurrency})
                                            </p>
                                            <p className="text-3xl font-bold text-gmail-blue mt-2">
                                                {grandTotal.toFixed(2)}
                                            </p>
                                        </div>
                                        <div>
                                            <Input
                                                label={`Amount Paid to Vendor Now (${selectedCurrency})`}
                                                type="number"
                                                step="0.01"
                                                value={paidAmount}
                                                onChange={(e) => setPaidAmount(e.target.value)}
                                                error={formErrors.paidAmount}
                                                placeholder="0.00"
                                                required={!isCreditReceipt}
                                                disabled={isCreditReceipt}
                                            />
                                            {isCreditReceipt && (
                                                <p className="text-sm text-gray-600 mt-2">
                                                    Full amount will be recorded as payable (Accounts Payable).
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end mt-8">
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        size="lg"
                                        disabled={isSubmitting || loadingData}
                                        className="flex items-center gap-2"
                                    >
                                        {isSubmitting ? 'Recording Receipt...' : 'Record Goods Receipt'}
                                    </Button>
                                </div>
                            </>
                        )}
                    </form>
                )}
            </Card>
        </div>
    );
};

export default VendorGoodsReceipt;